import { cn } from '@/utils';

interface SJAvatarProps {
  src?: string | null;
  name?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeStyles = {
  sm: 'h-7 w-7 text-[0.625rem]',
  md: 'h-9 w-9 text-xs',
  lg: 'h-12 w-12 text-sm',
};

function getInitials(name?: string): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function SJAvatar({ src, name, size = 'md', className }: SJAvatarProps) {
  const initials = getInitials(name);

  return (
    <div
      className={cn(
        'inline-flex items-center justify-center rounded-full shrink-0',
        sizeStyles[size],
        className
      )}
    >
      {src ? (
        <img
          src={src}
          alt={name ?? 'Avatar'}
          className={cn('rounded-full object-cover', sizeStyles[size])}
        />
      ) : (
        <div className={cn('flex items-center justify-center rounded-full bg-primary-100 font-semibold text-primary-700', sizeStyles[size])}>
          {initials}
        </div>
      )}
    </div>
  );
}

interface SJAvatarGroupProps {
  avatars: Array<{ src?: string | null; name?: string }>;
  max?: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function SJAvatarGroup({ avatars, max = 4, size = 'md', className }: SJAvatarGroupProps) {
  const visible = avatars.slice(0, max);
  const overflow = avatars.length - max;

  return (
    <div className={cn('flex items-center -space-x-2', className)}>
      {visible.map((avatar, i) => (
        <div key={i} className="ring-2 ring-[var(--sj-surface)] rounded-full">
          <SJAvatar src={avatar.src} name={avatar.name} size={size} />
        </div>
      ))}
      {overflow > 0 && (
        <div
          className={cn(
            'flex items-center justify-center rounded-full bg-[var(--sj-surface-muted)] border-2 border-[var(--sj-surface)] font-medium text-[var(--sj-text-muted)]',
            sizeStyles[size]
          )}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
}
